import Image from 'next/image';
import React, { ReactNode, useCallback, useMemo } from 'react';

import { GetSimpleFooterResponse } from 'services/idm';

import {
	PUBLIC_CHANNEL,
	PUBLIC_GTM_ID_ENV,
	PUBLIC_SITENAME,
} from 'tools/environments';

import { PageTemplate } from '../patterns';
import styles from './layout.module.scss';

type LayoutProps = {
	children: ReactNode;
	footerData: GetSimpleFooterResponse;
	backgroundUrl?: string;
	playerUrl?: string;
};

const THEME = PUBLIC_CHANNEL || 'mancity';
// The number of images in the /images/${THEME}/players/ folder
const NUMBER_OF_PLAYERS = PUBLIC_SITENAME === 'palermo' ? 7 : 3;
const PLAYER_FALLBACK_SRC = `/images/${THEME}/players/player-1.webp`;
const COVER_STYLE = { objectFit: 'cover' as const };
const ANALYTICS_CONFIG = { gtmId: PUBLIC_GTM_ID_ENV };

export const Layout = ({
	children,
	footerData,
	backgroundUrl,
	playerUrl,
}: LayoutProps) => {
	const backgroundImage =
		backgroundUrl || `/images/${THEME}/signin-background.webp`;

	// Random player is picked once per mount (or when playerUrl changes).
	// useMemo stabilises the value across parent re-renders.
	const playerImage = useMemo(
		() =>
			playerUrl ||
			`/images/${THEME}/players/player-${
				Math.floor(Math.random() * NUMBER_OF_PLAYERS) + 1
			}.webp`,
		[playerUrl]
	);

	const handlePlayerImageError = useCallback(
		(e: React.SyntheticEvent<HTMLImageElement>) => {
			const img = e.currentTarget;
			img.onerror = null;
			img.src = PLAYER_FALLBACK_SRC;
		},
		[]
	);

	return (
		<PageTemplate footerData={footerData} config={ANALYTICS_CONFIG}>
			<div className={styles.backgroundWrapper}>
				<div className={styles.imageContainer}>
					<Image
						src={backgroundImage}
						alt=""
						className={styles.background}
						fill
						style={COVER_STYLE}
						priority
					/>
					<img
						src={playerImage}
						alt=""
						aria-hidden="true"
						className={styles.player}
						onError={handlePlayerImageError}
					/>
				</div>
				<div className="container">
					<div className="row">
						<div className="col-12">
							<div className={styles.container}>
								<div className={styles.formWrapper}>
									{children}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</PageTemplate>
	);
};
